import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo } from 'react'

import { useOpenWindow, useDebouncedValue } from '@krp/client-runtime'
import { platformMyResources } from '@krp/platform-ui'
import {
  AQSI_WINDOWS,
  COMMON_SEARCH_PAGE_SIZE,
  FEEDBACK_SCOPES,
  PICKER_PAGE_SIZE,
  RmkWorkspace,
  SEARCH_DEBOUNCE_MS,
  SEARCH_RETRY_POLL_MS,
  aqsiActiveSale,
  aqsiCommonGoods,
  aqsiGoods,
  aqsiHeldCarts,
  aqsiParkedCount,
  aqsiParkedSales,
  aqsiPrintSettings,
  createReceiptsTabActions,
  aqsiSale,
  aqsiSaleCheck,
  aqsiSaleOperation,
  cartDraftOf,
  createSaleActions,
  currentOperationOf,
  fastReceiptDismissed,
  fastReceiptFlashOf,
  fastReceiptNotesOf,
  type FastReceiptNote,
  makeSelectSaleVm,
  mineSeatOf,
  paymentAttemptOf,
  paymentFeedbackOf,
  receiptsAheadOf,
  resolveDraftOf,
  ridKey,
  saleCommandOf,
  saleLatePaymentRequested,
  saleUnparkRequested,
  saleOf,
  seatIntentOf,
  useClockBucket,
  useSaleProgress,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { MaintenanceGate } from '../../app/MaintenanceGate'
import type { RootState } from '../../app/types'
import { AQSI_MINE } from './aqsiMine'
import { useRmkModel } from './useRmkModel'
import { useTraceSession } from './useTraceSession'
import { AqsiBackgroundReceiptsBinding } from './AqsiBackgroundReceiptsBinding'
import { useRefetchOnReconnect } from '../../app/connection'

const NO_FAST_NOTES: readonly FastReceiptNote[] = []

/**
 * Binding окна «РМК» источника (UI-RMK-FLOW, RP-FB22): на адресе источника одно окно — «Начало работы» → ожидание
 * открытия смены → порог смены → экран продажи. Данные кассового потока — `useRmkModel` (кассы кабинета, моя работа
 * из нашей базы, смена, связь); здесь — продажа: корзина из `sales/active` (сервер), отложенные чеки — тоже с
 * сервера; в хранилище — только намерение человека.
 */
export function AqsiRmkBinding() {
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const { resourceId, rmkVm, rmkActions, mine, selectRegisters, selectMySession } = useRmkModel()
  // Трассировка продаж этого источника (RP-SALE-TRACE): сбор точек и флаг подробного режима — только при праве `sales.trace`.
  useTraceSession(resourceId)
  // «Ждём 3 мин», «доступно через 2 мин»: vm окна пересчитывается с ходом времени (RP-FB35 K7).
  const now = useClockBucket()

  // ——— продажа места: корзина, оплата, поиск, отложенные ———
  const seatKey = mineSeatOf(mine)
  const registerKey = seatKey?.registerKey ?? null
  const deviceId = seatKey?.deviceId ?? null
  const seatArgs = useMemo(
    () => (registerKey === null || deviceId === null ? null : { resourceId, registerKey, deviceId }),
    [deviceId, resourceId, registerKey],
  )
  const seat = useAppSelector((state: RootState) =>
    seatIntentOf(state.aqsi.sale, seatArgs ?? { resourceId, registerKey: '' }),
  )
  const searchedText = useDebouncedValue(seat.searchText.trim(), SEARCH_DEBOUNCE_MS)
  const searchArgs = useMemo(
    // Строка поиска РМК ищет в общем каталоге — тем же поиском, что окно «Номенклатура»; карточки кассы подставляются по ключам.
    () => (seatArgs === null || searchedText === '' ? null : { resourceId, text: searchedText, mode: 'text' as const, limit: COMMON_SEARCH_PAGE_SIZE }),
    [resourceId, searchedText, seatArgs],
  )
  // «☰ Каталог»: страница снимка с поиском службы по всему снимку (тот же поиск, что у строки РМК).
  const catalogArgs = useMemo(
    () =>
      seatArgs === null
        ? null
        : { resourceId, page: seat.pickerPage, pageSize: PICKER_PAGE_SIZE, ...(seat.pickerQuery.trim() !== '' ? { search: seat.pickerQuery.trim() } : {}) },
    [resourceId, seat.pickerPage, seat.pickerQuery, seatArgs],
  )
  // Время снимка каталога («Цены кассы на 14:32») — малая страница, а не весь каталог.
  const snapshotArgs = useMemo(() => (seatArgs === null ? null : { resourceId, page: 0, pageSize: 1 }), [resourceId, seatArgs])
  const activeSubscription = aqsiActiveSale.useQuerySubscription(seatArgs ?? skipToken, { refetchOnMountOrArgChange: true })
  aqsiHeldCarts.useQuerySubscription(seatArgs ?? skipToken)
  // Отложенные продажи моей кассы (деньги взяты, чек не пробит): заметка «Предыдущая продажа отложена…» со ссылкой на список.
  const parkedArgs = useMemo(() => (seatArgs === null ? null : { resourceId, deviceId: seatArgs.deviceId }), [resourceId, seatArgs])
  aqsiParkedCount.useQuerySubscription(parkedArgs ?? skipToken, { pollingInterval: 30_000 })
  // Быстрая оплата: настройка кассы и продажа, только что ушедшая в фон (галочка «Оплата прошла»).
  const printSettingsArgs = useMemo(() => (seatArgs === null ? null : { resourceId, deviceId: seatArgs.deviceId }), [resourceId, seatArgs])
  aqsiPrintSettings.useQuerySubscription(printSettingsArgs ?? skipToken)
  const fastEnabled = useAppSelector((state: RootState) => printSettingsArgs !== null && aqsiPrintSettings.select(printSettingsArgs)(state).data?.fastPayment === true)
  const fastNotes = useAppSelector((state: RootState) => (seatArgs === null ? NO_FAST_NOTES : fastReceiptNotesOf(state.aqsi.fastReceipts, seatArgs)))
  const flashSaleId = useAppSelector((state: RootState) => (seatArgs === null ? null : fastReceiptFlashOf(state.aqsi.fastReceipts, seatArgs)))
  const backgroundChecking = useAppSelector((state) => {
    const rows = parkedArgs === null ? undefined : aqsiParkedSales.select(parkedArgs)(state).data
    if (rows === undefined) return false
    // Чек печатается в фоне (быстрая оплата) — список читается чаще: оплата следующей продажи ждёт его конца.
    return rows.some((sale) => ['InProgress', 'Unknown'].includes(sale.payment)) || (fastEnabled && receiptsAheadOf(rows, Date.now()).printing > 0)
  })
  aqsiParkedSales.useQuerySubscription(parkedArgs ?? skipToken, { pollingInterval: backgroundChecking ? 1_000 : 5_000 })
  // Сбой запроса выдачи — не «ничего не найдено»: пока связи нет, запрос пробует снова сам (RP-SALE-FAST, шаг 7а, К-108).
  const searchFailed = useAppSelector((state: RootState) => searchArgs !== null && aqsiCommonGoods.select(searchArgs)(state).isError)
  aqsiCommonGoods.useQuerySubscription(searchArgs ?? skipToken, { pollingInterval: searchFailed ? SEARCH_RETRY_POLL_MS : 0 })
  aqsiGoods.useQuerySubscription(catalogArgs ?? skipToken)
  aqsiGoods.useQuerySubscription(snapshotArgs ?? skipToken)

  const selectActive = useMemo(() => (seatArgs === null ? null : aqsiActiveSale.select(seatArgs)), [seatArgs])
  const activeResult = useAppSelector((state: RootState) => selectActive?.(state))
  // Сбой связи показывает общее окно приложения; связь вернулась — продажа кассы перечитывается сразу.
  useRefetchOnReconnect(activeSubscription.refetch, seatArgs !== null)
  const activity = activeResult?.data
  const saleOperationId = currentOperationOf(activity)
  // Проверка корзины службой (С2): для сохранённой версии продажи; правка корзины даёт новый запрос.
  const currentSale = saleOf(activity)
  const checkArgs = useMemo(
    () => (currentSale === null ? null : { resourceId, saleId: currentSale.id, saleVersion: currentSale.version }),
    [currentSale, resourceId],
  )
  aqsiSaleCheck.useQuerySubscription(checkArgs ?? skipToken)
  const saleOperationArgs = useMemo(
    () => (saleOperationId === null ? null : { resourceId, operationId: saleOperationId }),
    [resourceId, saleOperationId],
  )
  const finishedArgs = useMemo(
    () => {
      const saleId = seat.finishedSaleId ?? flashSaleId
      return saleId === null ? null : { resourceId, saleId }
    },
    [flashSaleId, resourceId, seat.finishedSaleId],
  )
  aqsiSaleOperation.useQuerySubscription(saleOperationArgs ?? skipToken)
  aqsiSale.useQuerySubscription(finishedArgs ?? skipToken)

  const selectSaleVm = useMemo(() => {
    const selectPackageVm = makeSelectSaleVm()
    const selectSources = platformMyResources.select(AQSI_MINE.rmk)
    const selectSellers = platformMyResources.select(AQSI_MINE.sales)
    const selectPriceEditors = platformMyResources.select(AQSI_MINE.price)
    const selectCatalog = catalogArgs === null ? null : aqsiGoods.select(catalogArgs)
    const selectSearch = searchArgs === null ? null : aqsiCommonGoods.select(searchArgs)
    const selectSnapshot = snapshotArgs === null ? null : aqsiGoods.select(snapshotArgs)
    const selectHeld = seatArgs === null ? null : aqsiHeldCarts.select(seatArgs)
    const selectParkedCount = parkedArgs === null ? null : aqsiParkedCount.select(parkedArgs)
    const selectBackgroundSales = parkedArgs === null ? null : aqsiParkedSales.select(parkedArgs)
    const selectOperation = saleOperationArgs === null ? null : aqsiSaleOperation.select(saleOperationArgs)
    const selectFinished = finishedArgs === null ? null : aqsiSale.select(finishedArgs)
    const selectCheck = checkArgs === null ? null : aqsiSaleCheck.select(checkArgs)
    const feedbackKey = seatArgs === null ? null : ridKey(resourceId, FEEDBACK_SCOPES.sale(seatArgs.registerKey))
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        sources: selectSources(state),
        sellers: selectSellers(state),
        priceEditors: selectPriceEditors(state),
        mySession: selectMySession(state),
        registers: selectRegisters(state),
        check: selectCheck?.(state),
        activeSale: selectActive?.(state),
        operation: selectOperation?.(state),
        finishedSale: selectFinished?.(state),
        goods: selectCatalog?.(state),
        searchGoods: selectSearch?.(state),
        searchedText: searchArgs?.text ?? '',
        heldCarts: selectHeld?.(state),
        parkedCount: selectParkedCount?.(state),
        backgroundSales: selectBackgroundSales?.(state),
        snapshot: selectSnapshot?.(state),
        seat: seatIntentOf(state.aqsi.sale, seatArgs ?? { resourceId, registerKey: '' }),
        // Очередь корзины: несохранённая правка и её отправка (RP-SALE-FAST).
        draft: seatArgs === null ? undefined : cartDraftOf(state.aqsi.cart, seatArgs),
        command: seatArgs === null ? undefined : saleCommandOf(state.aqsi.saleCommands, seatArgs),
        feedback: feedbackKey === null ? undefined : state.aqsi.feedback.byKey[feedbackKey],
        // RP-PAY-ONE: попытка оплаты кассы, её ход и черновик ручной сверки текущей операции — окно оплаты.
        attempt: seatArgs === null ? undefined : paymentAttemptOf(state.aqsi.paymentAttempts, seatArgs),
        attemptFeedback: seatArgs === null ? undefined : paymentFeedbackOf(state.aqsi.paymentAttempts, seatArgs),
        resolveDraft: saleOperationId === null ? undefined : resolveDraftOf(state.aqsi.sale, resourceId, saleOperationId),
        fast: { enabled: fastEnabled, flashSaleId },
        now,
      })
  }, [fastEnabled, flashSaleId, now, parkedArgs, catalogArgs, checkArgs, finishedArgs, resourceId, saleOperationArgs, saleOperationId, searchArgs, seatArgs, selectActive, selectMySession, selectRegisters, snapshotArgs])
  const saleVm = useAppSelector(selectSaleVm)
  useSaleProgress(
    resourceId,
    seatArgs,
    saleVm.operation,
    dispatch,
    saleVm.payment?.stage === 'checking' || saleVm.payment?.stage === 'receipt',
    saleVm.payment?.stage === 'no-receipt',
  )

  const saleActions = useMemo(
    () =>
      createSaleActions(saleVm, dispatch, {
        // Список отложенных продаж — во вкладке «Чеки»: выбрать вкладку и открыть окно.
        openParked: () => {
          createReceiptsTabActions(resourceId, dispatch).chooseTab('parked')
          openWindow(AQSI_WINDOWS.receipts, { rid: resourceId })
        },
        openScanner: () => openWindow(AQSI_WINDOWS.saleScanner, { rid: resourceId }),
        // Возврат на кассе держит продажу: окно возврата по исходному чеку — поверх РМК.
        // Пустой номер чека - возврат по слипу без чека (RP-FB74): его окно - «Возврат по слип-чеку».
        openRefund: (receiptId) =>
          receiptId === ''
            ? openWindow(AQSI_WINDOWS.slipRefund, { rid: resourceId })
            : openWindow(AQSI_WINDOWS.refund, { rid: resourceId, key: receiptId }),
      }),
    [dispatch, openWindow, resourceId, saleVm],
  )
  // RP-MNT: во время обновления РМК закрыта замком; открытый чек разрешаем довести (слив), новую продажу — нет. Подпись — для пульта.
  const placeLabel = [saleVm.source.name, deviceId === null ? null : `касса ${deviceId}`, mine?.cashierName ?? null]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ')
  return (
    <MaintenanceGate resourceId={resourceId} label={placeLabel === '' ? 'РМК AQSI' : placeLabel}>
      <RmkWorkspace rmk={rmkVm} rmkActions={rmkActions} sale={saleVm} saleActions={saleActions}
        backgroundWork={parkedArgs === null ? undefined : <AqsiBackgroundReceiptsBinding resourceId={resourceId} deviceId={parkedArgs.deviceId} fastNotes={fastNotes} onFastDismiss={registerKey === null ? undefined : (saleId) => dispatch(fastReceiptDismissed({ resourceId, registerKey, saleId }))} activeSaleId={saleVm.saleId} onOpen={() => saleActions.openParked?.()}
          onResolve={(operationId) => openWindow(AQSI_WINDOWS.saleResolve, { rid: resourceId, operationId })}
          // Запоздавший слип отложенной продажи: решение уходит командой с её версией; ход и отказ — в баннере команд окна продажи.
          onLatePayment={registerKey === null ? undefined : (sale, paid) => dispatch(saleLatePaymentRequested({ resourceId, registerKey, deviceId: parkedArgs.deviceId, saleId: sale.id, expectedVersion: sale.version, paid, background: true }))}
          latePaymentBusy={saleVm.feedback?.command === 'sale.late-payment' && saleVm.feedback.phase === 'sending'}
          // Чек с проблемой: продажа возвращается на кассу, и «Повторить проведение»/«Отложить» работают в окне оплаты, как раньше.
          onReturn={registerKey === null ? undefined : (sale) => dispatch(saleUnparkRequested({ resourceId, registerKey, deviceId: parkedArgs.deviceId, saleId: sale.id, expectedVersion: sale.version }))} />} />
    </MaintenanceGate>
  )
}
